/**
 * Phase 408 wave 3 task 3.2 (ADR-089) — the operator-console chat panel.
 *
 * A signed-in operator talks to their tenant's agent here: grounded Q&A plus
 * propose → confirm → execute gated writes, with no Teams or Google Chat estate in the
 * picture. The identity on the JWT is the sender, the owner of the conversation and the
 * actor on any write it confirms — there is no sender field anywhere on this screen
 * because there is nothing for one to say.
 *
 * Three things this screen deliberately does NOT do:
 *
 *   1. It never fabricates a tenant list. The picker is `/me` `allowed_tenants` and
 *      nothing else; the id it sends is a locator the engine re-checks against the
 *      principal (OperatorTenantGuard), so the worst a wrong pick can earn is a refusal.
 *   2. It never invents state. Sends and decisions wait for the server and re-read
 *      (usePostChatMessage.ts) — what is on screen is what the conversation store holds.
 *   3. It never offers a dead button. A gated card is actionable IF AND ONLY IF the read
 *      API reports `confirm_state: "pending"`; every other state renders as prose about
 *      what happened, which is also how a decision taken on ANOTHER surface (or by a
 *      colleague) shows up here.
 *
 * Colour is entirely @ardept/design-tokens vars (POLISH-01 hex fence). Live render truth
 * is wave 4's UAT bar — the console's test tree is node-only by design.
 */
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  DiffBlock,
  EmptyState,
  KV,
  LastUpdated,
  NavRail,
  PageHeader,
  Select,
  Textarea,
} from "@ardept/design-system";
import { useMe } from "../api/queries.js";
import {
  isConversationWorking,
  isThreadWorking,
  useChatConversations,
  useChatMessages,
} from "../api/chatQueries.js";
import {
  rowControlsRefusal,
  usePostChatDecision,
  usePostChatMessage,
  type ChatDecisionVars,
} from "../api/usePostChatMessage.js";
import {
  CARD_CLARIFICATION_SELECTION,
  CARD_DECISION,
  CARD_WRITE_CONFIRM,
  CONFIRM_STATE_LABELS,
  DIRECTION_INBOUND,
  isActionable,
} from "../api/chatRoutes.js";
import { DIFF_PREVIEW_KIND, classifyDiffLine, splitDiff } from "../api/diffLines.js";
import { readServerRefusal } from "../api/serverRefusal.js";
import type {
  ConsoleChatCardPreview,
  ConsoleChatCardRow,
  ConsoleChatDecisionRequest,
  ConsoleChatDecisionResponse,
  ConsoleChatMessageDto,
} from "../api/types.js";
import "./ChatScreen.css";
import "./screens.css";

/** URL param names — the console's D-URL-01 convention: the URL is the state, so a
 *  conversation is linkable and a reload lands where the operator was. */
const PARAM_TENANT = "tenant";
const PARAM_CONVERSATION = "conversation";

/**
 * The inbound replay key for one composed draft. `crypto.randomUUID` is present in every
 * browser this console supports; the fallback exists only so a non-secure-context dev
 * server degrades to a still-unique id rather than throwing (this is an idempotency key,
 * never a credential).
 */
function newClientMessageId(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `console-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** "14:32" in the viewer's locale — a chat thread wants the clock, not a full date. */
function formatTurnTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

export function ChatScreen(): JSX.Element {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { data: me } = useMe();

  const tenantOptions = me?.allowed_tenants ?? [];
  const tenantId = params.get(PARAM_TENANT) ?? "";
  const conversationId = params.get(PARAM_CONVERSATION) ?? "";

  const setParam = React.useCallback(
    (name: string, value: string | null, replace = false) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === null) next.delete(name);
          else next.set(name, value);
          // Switching tenant cannot keep a conversation: conversations are scoped to a
          // tenant AND an owner, so the old id would simply read empty in the new one.
          if (name === PARAM_TENANT) next.delete(PARAM_CONVERSATION);
          return next;
        },
        { replace },
      );
    },
    [setParams],
  );

  // Auto-select the first allowed tenant once /me resolves (WeeklyActivityScreen's shape).
  // `replace` so the auto-pick does not push a history entry the Back button has to eat.
  useEffect(() => {
    const first = tenantOptions[0];
    if (!tenantId && first) setParam(PARAM_TENANT, first.id, true);
  }, [tenantOptions, tenantId, setParam]);

  const conversations = useChatConversations(tenantId);
  const thread = useChatMessages(tenantId, conversationId);
  const send = usePostChatMessage(tenantId);
  const decision = usePostChatDecision(tenantId, conversationId);

  const [draft, setDraft] = useState("");
  // One replay key per composed draft: pressing Send again after a failure re-sends the
  // same message instead of asking the agent twice. Cleared only on a successful send.
  const draftIdRef = useRef<string | null>(null);
  const threadEndRef = useRef<HTMLDivElement | null>(null);
  // #2370: the last send the server accepted, so the working line holds across the gap between
  // the 202 and the first read of the thread (see isThreadWorking).
  const [acceptedSend, setAcceptedSend] = useState<
    { conversationId: string; acceptedAt: number } | undefined
  >(undefined);

  const messages = thread.data?.messages ?? [];
  const working = isThreadWorking({
    conversationId,
    messages,
    turnOpen: thread.data?.working ?? false,
    threadUpdatedAt: thread.dataUpdatedAt,
    pendingSend: send.isPending
      ? { conversationId: send.variables?.conversationId ?? "" }
      : undefined,
    acceptedSend,
  });

  // Once the thread has been read after the accepted send, the thread itself decides: drop the
  // marker so a later return to this conversation (its cache collected) never re-shows it.
  useEffect(() => {
    if (
      acceptedSend &&
      acceptedSend.conversationId === conversationId &&
      thread.dataUpdatedAt >= acceptedSend.acceptedAt
    ) {
      setAcceptedSend(undefined);
    }
  }, [acceptedSend, conversationId, thread.dataUpdatedAt]);

  // Keep the newest turn in view as replies land.
  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, working]);

  function submitSend(): void {
    const text = draft.trim();
    if (!text || !tenantId || send.isPending) return;
    if (!draftIdRef.current) draftIdRef.current = newClientMessageId();

    send.mutate(
      {
        body: text,
        clientMessageId: draftIdRef.current,
        ...(conversationId ? { conversationId } : {}),
      },
      {
        onSuccess: (res) => {
          setDraft("");
          draftIdRef.current = null;
          setAcceptedSend({ conversationId: res.conversation_id, acceptedAt: Date.now() });
          // A send with no conversation MINTS one; adopt the id the server echoed so the
          // poll starts on the thread this message actually landed in.
          if (res.conversation_id !== conversationId) {
            setParam(PARAM_CONVERSATION, res.conversation_id);
          }
        },
      },
    );
  }

  function onComposerKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    // Enter sends, Shift+Enter is a newline — the convention every chat surface uses.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submitSend();
    }
  }

  const operatorDisplay = {
    name: me?.email
      ? (me.email.split("@")[0] ?? me.email)
          .replace(/[._-]/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : "Operator",
    email: me?.email ?? "",
  };

  const conversationRows = conversations.data?.conversations ?? [];
  const hasTenant = !!tenantId;

  return (
    <div className="app">
      <NavRail active="chat" operator={operatorDisplay} onNavigate={navigate} />
      <main className="main">
        <PageHeader
          title="Chat"
          subtitle="Ask your tenant's agent a question, or confirm a write it proposes. Every message and decision is attributed to your operator identity."
        />
        {thread.dataUpdatedAt > 0 && conversationId && (
          <LastUpdated timestamp={thread.dataUpdatedAt} />
        )}

        {/* Tenant picker — /me allowed_tenants ONLY. */}
        {tenantOptions.length > 0 && (
          <div className="chat-filter">
            <label className="chat-filter__label" htmlFor="chat-tenant-select">
              Tenant
            </label>
            <select
              id="chat-tenant-select"
              className="chat-filter__select"
              value={tenantId}
              disabled={tenantOptions.length === 1}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setParam(PARAM_TENANT, e.target.value)
              }
            >
              {tenantOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.display_name || t.slug}
                </option>
              ))}
            </select>
          </div>
        )}

        {me && tenantOptions.length === 0 && (
          <EmptyState
            iconName="octagon-alert"
            title="No tenant access"
            body="Ask an administrator to grant you access to a tenant before starting a conversation."
          />
        )}

        {hasTenant && (
          <div className="chat-grid">
            {/* LEFT: this principal's conversations in this tenant. */}
            <aside className="chat-list">
              <div className="chat-panel__head">
                <span>Your conversations</span>
                <span className="chat-panel__meta">{conversationRows.length}</span>
              </div>
              <Button
                className="chat-list__new"
                variant="ghost"
                onClick={() => setParam(PARAM_CONVERSATION, null)}
                disabled={!conversationId}
              >
                New conversation
              </Button>
              {conversationRows.length === 0 && (
                <p className="chat-list__empty">
                  No conversations yet. Ask a question to start one.
                </p>
              )}
              {conversationRows.map((c) => (
                <button
                  key={c.conversation_id}
                  className={`chat-list__row ${
                    c.conversation_id === conversationId ? "is-active" : ""
                  }`}
                  aria-current={c.conversation_id === conversationId ? "true" : undefined}
                  onClick={() => setParam(PARAM_CONVERSATION, c.conversation_id)}
                >
                  <span className="chat-list__preview">
                    {c.last_body?.trim() || "(no messages)"}
                  </span>
                  <span className="chat-list__meta">
                    {formatTurnTime(c.last_message_at)} · {c.message_count} turns
                  </span>
                  {isConversationWorking(c) && <WorkingLine label="Working…" compact />}
                </button>
              ))}
              {conversations.data?.has_more && (
                <p className="chat-list__empty">
                  Older conversations exist beyond this page.
                </p>
              )}
            </aside>

            {/* RIGHT: the thread + composer. */}
            <section className="chat-thread">
              <div className="chat-panel__head">
                <span>{conversationId ? "Conversation" : "New conversation"}</span>
                {conversationId && <span className="chat-panel__meta">{conversationId}</span>}
              </div>

              <div className="chat-thread__scroll">
                {thread.isLoading && conversationId && (
                  <p className="chat-note">Loading the conversation...</p>
                )}

                {thread.error && (
                  <div className="chat-error" role="status">
                    Could not read this conversation. The panel keeps polling — if this
                    persists, check that you still have access to this tenant.
                  </div>
                )}

                {thread.data?.truncated && (
                  <p className="chat-note">
                    This conversation is longer than the panel reads in one pass — the
                    most recent turns are below.
                  </p>
                )}

                {!thread.isLoading && !thread.error && messages.length === 0 && (
                  <EmptyState
                    iconName="message-square"
                    title={conversationId ? "No messages yet" : "Start a conversation"}
                    body="Ask a question below. The agent answers on this thread, and any write it proposes waits for your confirmation."
                  />
                )}

                {messages.map((m) => (
                  <ChatTurn
                    key={m.id}
                    message={m}
                    disabled={decision.isPending}
                    onDecide={(vars, onAnswered) =>
                      decision.mutate(vars, onAnswered ? { onSuccess: onAnswered } : undefined)
                    }
                  />
                ))}

                {/* #2370 — the working line: from the moment Send is pressed until the
                    platform's next turn lands (isThreadWorking). It carries the design
                    system's agent-thinking signal (a coral dot pulsing), so it reads as
                    activity rather than as a caption. */}
                {working && <WorkingLine label="Working on a reply…" />}

                {decision.isError && (
                  <div className="chat-error" role="status">
                    Could not record that decision. Reload the conversation to see its
                    current state before trying again.
                  </div>
                )}

                <div ref={threadEndRef} />
              </div>

              {/* Composer */}
              <div className="chat-composer">
                <Textarea
                  className="chat-composer__input"
                  value={draft}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDraft(e.target.value)}
                  onKeyDown={onComposerKeyDown}
                  placeholder="Ask a question. Enter sends, Shift+Enter adds a line."
                  rows={3}
                  aria-label="Message"
                />
                <div className="chat-composer__actions">
                  {/* #2054 — the engine's refusal, in the engine's own words (the
                      OutstandingItemsPanel precedent). An entitlement refusal's 403 `message`
                      is the one refusal sentence every chat surface shows, and pressing Send
                      again cannot change it — so a refusal never offers the retry line.
                      `readServerRefusal` returns null for anything that is not an engine
                      refusal (a network failure, a 5xx with an HTML body), and only then is
                      "press Send to retry" true. */}
                  {send.isError && (
                    <span className="chat-composer__error" role="alert">
                      {readServerRefusal(send.error)?.message ??
                        "Message not sent. Press Send to retry the same message."}
                    </span>
                  )}
                  <Button
                    variant="primary"
                    onClick={submitSend}
                    disabled={!draft.trim() || send.isPending}
                  >
                    {send.isPending ? "Sending..." : "Send"}
                  </Button>
                </div>
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

/**
 * #2370 — "the platform is working on this". The design system's agent-thinking state: a coral
 * dot pulsing on the shared `ard-pulse` loop (design-tokens: `--agent-thinking`,
 * `--motion-pulse-duration`), with the words beside it so the state never rests on motion alone
 * (reduced motion stops the pulse and leaves a steady dot). `compact` is the conversation-list
 * form of the same signal.
 */
function WorkingLine({ label, compact = false }: { label: string; compact?: boolean }): JSX.Element {
  return (
    // The thread's line is the live region; the list form is a label on a row, not an
    // announcement (one status per screen, not one per conversation).
    <span
      className={`chat-working${compact ? " chat-working--compact" : ""}`}
      role={compact ? undefined : "status"}
      aria-live={compact ? undefined : "polite"}
    >
      <span className="chat-working__dot" aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}

/**
 * One turn. The renderer reads `direction` to place the bubble and `card.card_kind` to
 * decide what else the turn carries; `actor` is displayed, never parsed.
 */
function ChatTurn({
  message,
  disabled,
  onDecide,
}: {
  message: ConsoleChatMessageDto;
  disabled: boolean;
  /** `onAnswered` hears the engine's answer to THIS tap (card #3140 — a row-controls refusal). */
  onDecide: (vars: ChatDecisionVars, onAnswered?: (res: ConsoleChatDecisionResponse) => void) => void;
}): JSX.Element {
  const inbound = message.direction === DIRECTION_INBOUND;
  const card = message.card;
  const kind = card?.card_kind;
  const token = card?.form_token;
  const preview = card?.preview;
  const rows = card?.rows;
  const live = isActionable(message.confirm_state);
  const spentLabel =
    message.confirm_state && !live
      ? CONFIRM_STATE_LABELS[message.confirm_state]
      : undefined;

  // Card #3140 — the operator's choices on a live write_confirm card's controlled rows, and the
  // engine's reason when it refused them. Local to this turn (keyed by message id), so a re-read
  // of the thread never resets an edit, and a refusal keeps every edit for the next try.
  const [controls, setControls] = useState<RowControlState>(() => initialRowControlState(rows));
  const [refusal, setRefusal] = useState<string | null>(null);

  function decide(vars: ChatDecisionVars): void {
    setRefusal(null);
    onDecide(vars, (res) => setRefusal(rowControlsRefusal(res)));
  }

  return (
    <article className={`chat-turn ${inbound ? "chat-turn--in" : "chat-turn--out"}`}>
      <header className="chat-turn__head">
        <span className="chat-turn__who">{inbound ? "You" : "Agent"}</span>
        <span className="chat-turn__time" title={message.created_at}>
          {formatTurnTime(message.created_at)}
        </span>
      </header>

      <div className="chat-turn__body">{message.body}</div>

      {/* A gated write: Confirm / Cancel, live only while the token is pending. */}
      {kind === CARD_WRITE_CONFIRM && (
        <div className="chat-card">
          {card?.prompt && <p className="chat-card__prompt">{card.prompt}</p>}
          {/* Phase 482 (ADR-098, AC-5) — the engine's own account of the write, between the
              prompt (the model's account) and the buttons. It renders on a SPENT card too:
              "what did I actually approve?" is the question the receipt has to answer.
              Card #2932 — every kind renders: a diff as a diff, anything else as its text. */}
          {preview && <WriteConfirmPreview preview={preview} />}
          {/* Card #3132 / #3140 — the fields the write changes. While the card is live a controlled
              row is its on-card controls (untick, edit, pick — the Teams card's parity) and Confirm
              sends what the operator changed; a spent card reads them as plain text. */}
          {live && token ? (
            <>
              {/* The engine's own reason for a refused edit, in its own words, under the rows;
                  the card stays live with the edits kept. Cleared by the next Confirm / Cancel. */}
              <WriteConfirmLiveRows
                rows={rows ?? []}
                state={controls}
                onChange={setControls}
                disabled={disabled}
                refusal={refusal}
              />
              <div className="chat-card__actions">
                <Button
                  variant="primary"
                  disabled={disabled}
                  onClick={() => decide(confirmDecisionVars(token, rows, controls))}
                >
                  Confirm
                </Button>
                <Button
                  variant="ghost"
                  disabled={disabled}
                  onClick={() => decide({ formToken: token, confirm: false })}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <>
              {rows && <WriteConfirmRows rows={rows} />}
              <p className="chat-card__spent">{spentLabel ?? "This card is no longer answerable."}</p>
            </>
          )}
        </div>
      )}

      {/* A bounded clarification: one button per option, carrying the option id. */}
      {kind === CARD_CLARIFICATION_SELECTION && (
        <div className="chat-card">
          {card?.prompt && <p className="chat-card__prompt">{card.prompt}</p>}
          {live && token ? (
            <div className="chat-card__actions chat-card__actions--stack">
              {(card?.options ?? []).map((opt) => (
                <Button
                  key={opt.id}
                  variant="ghost"
                  disabled={disabled}
                  onClick={() => onDecide({ formToken: token, selected: opt.id })}
                >
                  {opt.label}
                  {opt.sublabel ? ` — ${opt.sublabel}` : ""}
                </Button>
              ))}
            </div>
          ) : (
            <p className="chat-card__spent">{spentLabel ?? "This card is no longer answerable."}</p>
          )}
        </div>
      )}

      {/* The receipt the decisions POST persisted — informational by construction: it
          carries no form_token, so the read API attaches no confirm_state to it. */}
      {kind === CARD_DECISION && card?.connector && (
        <p className="chat-turn__receipt">Connector: {card.connector}</p>
      )}
    </article>
  );
}

/**
 * Card #3132 — a write_confirm card's rows, read-only, one design-system `KV` per row: the label, and
 * `before → after` (or just `after`) — for a picker row, the current choice's label. The card is jsonb, so
 * every member is narrowed at the point of use: a row that is not an object with a string label renders
 * nothing rather than "undefined" beside a write someone is about to approve.
 *
 * Exported for `ChatScreen.rows.test.ts`, which renders it; nothing else imports it.
 */
export function WriteConfirmRows({ rows }: { rows: ConsoleChatCardRow[] }): JSX.Element | null {
  const shown = shownRows(rows);
  if (shown.length === 0) return null;
  return (
    <div className="chat-card__rows">
      {shown.map((row) => (
        <KV key={row.key} label={row.label} value={rowValueText(row)} />
      ))}
    </div>
  );
}

/** The rows a card can show: the card is jsonb, so a row that is not an object with a non-empty string
 *  label (or a `rows` that is not an array) is dropped rather than drawn as "undefined". */
function shownRows(rows: unknown): ConsoleChatCardRow[] {
  return (Array.isArray(rows) ? rows : []).filter(
    (row): row is ConsoleChatCardRow =>
      typeof row === "object" && row !== null && typeof row.label === "string" && row.label !== "",
  );
}

/** The value text of one row — the `WriteConfirmRow.ValueText` rule (`before → after`, or `after` alone),
 *  except a picker row, which reads as its current option's label. */
export function rowValueText(row: ConsoleChatCardRow): string {
  if (Array.isArray(row.options) && typeof row.value === "string") {
    const chosen = row.options.find((o) => o && o.value === row.value);
    if (chosen && typeof chosen.label === "string" && chosen.label !== "") return chosen.label;
  }
  const after = typeof row.after === "string" ? row.after : "";
  const before = typeof row.before === "string" ? row.before.trim() : "";
  return before === "" ? after : `${before} → ${after}`;
}

// ── Card #3140 — the live card's row controls (the Teams card's parity) ─────────────────────────

/** The most characters an edited row may hold — `WriteConfirmRow.MaxEditableChars` (the engine refuses
 *  a longer text; the text box stops at it). Pinned to the engine's constant by
 *  test/write-confirm-controls.fence.test.ts. */
export const WRITE_CONFIRM_MAX_EDITABLE_CHARS = 2000;

/** The words beside an optional row's tick box when its text is edited below it —
 *  `WriteConfirmCard.SaveThisRowTitle` (the Teams card's wording). */
export const SAVE_THIS_ROW_TITLE = "Save this";

/** The name prefixes of a row's controls — the Teams card's input ids (`WriteConfirmRow.ToggleInputPrefix`
 *  / `TextInputPrefix` / `PickInputPrefix`), so one row reads the same on both surfaces. */
export const ROW_TOGGLE_PREFIX = "rc_on_";
export const ROW_TEXT_PREFIX = "rc_txt_";
export const ROW_PICK_PREFIX = "rc_pick_";

/** The operator's choices on a live card's controlled rows, keyed by row key. */
export interface RowControlState {
  /** An optional row: ticked (written) or not. */
  on: Record<string, boolean>;
  /** An editable row: the text in its box, as typed. */
  txt: Record<string, string>;
  /** A picker row: the chosen option's value. */
  pick: Record<string, string>;
}

type RowControls = NonNullable<ConsoleChatDecisionRequest["row_controls"]>;

/** A picker row's options, each a `{value, label}` pair of non-empty strings (the card is jsonb). */
function pickerOptions(row: ConsoleChatCardRow): { value: string; label: string }[] {
  if (!Array.isArray(row.options)) return [];
  return row.options.filter(
    (o): o is { value: string; label: string } =>
      typeof o === "object" &&
      o !== null &&
      typeof o.value === "string" &&
      o.value !== "" &&
      typeof o.label === "string" &&
      o.label !== "",
  );
}

/**
 * Which controls a row draws — the Teams card's `RenderControlledRow` rule: a row with options is a
 * picker and nothing else; otherwise an `optional` row gets a tick box and an `editable` row a text
 * box (both, for a row that is both). A row with no control, or no key to name its controls by,
 * stays plain text.
 */
function rowControlKind(row: ConsoleChatCardRow): { picker: boolean; toggle: boolean; text: boolean } {
  const none = { picker: false, toggle: false, text: false };
  if (typeof row.key !== "string" || row.key === "") return none;
  if (pickerOptions(row).length > 0) return { picker: true, toggle: false, text: false };
  return { picker: false, toggle: row.optional === true, text: row.editable === true };
}

/** A row's text as the engine compares it (`ProposedWriteRowControls.NormaliseText`): CRLF folded, trimmed. */
function normaliseRowText(text: string): string {
  return text.replace(/\r\n/g, "\n").trim();
}

/** The card's defaults: every optional row ticked, every text box holding `after`, every picker on `value`. */
export function initialRowControlState(rows: unknown): RowControlState {
  const state: RowControlState = { on: {}, txt: {}, pick: {} };
  for (const row of shownRows(rows)) {
    const kind = rowControlKind(row);
    if (kind.picker) state.pick[row.key] = typeof row.value === "string" ? row.value : "";
    if (kind.toggle) state.on[row.key] = true;
    if (kind.text) state.txt[row.key] = typeof row.after === "string" ? row.after : "";
  }
  return state;
}

/**
 * What a Confirm sends as `row_controls`: ONLY what the operator changed, keyed like the Teams inputs.
 * An unticked row is `on: {key: false}` (and its text is not sent — the untick wins); a text that
 * differs from `after` once both are normalised is `txt: {key: text}` — a blanked box is sent as `""`,
 * which the engine reads as an untick (and is the only untick an editable row that is NOT optional
 * has); a choice other than `value` is `pick: {key: value}`. An empty bag is omitted, and `undefined`
 * comes back when nothing changed, so an untouched Confirm sends no `row_controls` at all.
 */
export function buildRowControls(rows: unknown, state: RowControlState): RowControls | undefined {
  const on: Record<string, boolean> = {};
  const txt: Record<string, string> = {};
  const pick: Record<string, string> = {};
  for (const row of shownRows(rows)) {
    const kind = rowControlKind(row);
    if (kind.picker) {
      const chosen = state.pick[row.key];
      if (typeof chosen === "string" && chosen !== row.value) pick[row.key] = chosen;
      continue;
    }
    if (kind.toggle && state.on[row.key] === false) {
      on[row.key] = false;
      continue;
    }
    if (kind.text) {
      const typed = state.txt[row.key];
      const after = typeof row.after === "string" ? row.after : "";
      if (typeof typed === "string" && normaliseRowText(typed) !== normaliseRowText(after)) {
        txt[row.key] = normaliseRowText(typed);
      }
    }
  }
  const controls: RowControls = {
    ...(Object.keys(on).length > 0 ? { on } : {}),
    ...(Object.keys(txt).length > 0 ? { txt } : {}),
    ...(Object.keys(pick).length > 0 ? { pick } : {}),
  };
  return Object.keys(controls).length > 0 ? controls : undefined;
}

/** The decision a live card's Confirm asks for: the token, `confirm: true`, and the changed row
 *  controls when there are any (absent — not empty — when there are none). */
export function confirmDecisionVars(token: string, rows: unknown, state: RowControlState): ChatDecisionVars {
  const rowControls = buildRowControls(rows, state);
  return { formToken: token, confirm: true, ...(rowControls ? { rowControls } : {}) };
}

/**
 * Card #3140 — a LIVE write_confirm card's rows: each controlled row as its on-card controls, built from
 * the design system (`Checkbox`, `Textarea`, `Select`) inside the same `KV` a spent card's row uses, so
 * the label sits where it always does; an uncontrolled row reads exactly as `WriteConfirmRows` draws it.
 * The wording is the Teams card's: an optional row's box says "Save this" when its text is edited
 * below it, else the row's value; an edited row with a current value is labelled "Label (now: …)".
 *
 * Hook-free: the turn owns the state and hears each change as the next state (`onChange`). An unticked
 * row's text box is disabled — its text is not written, so it is not offered for editing. `refusal` is
 * the engine's reason for a refused Confirm (`rowControlsRefusal`), shown under the rows as an alert
 * while the controls keep whatever the operator had set.
 *
 * Exported for `ChatScreen.controls.test.ts`, which renders it and drives its controls.
 */
export function WriteConfirmLiveRows({
  rows,
  state,
  onChange,
  disabled,
  refusal = null,
}: {
  rows: ConsoleChatCardRow[];
  state: RowControlState;
  onChange: (next: RowControlState) => void;
  disabled: boolean;
  refusal?: string | null;
}): JSX.Element | null {
  const shown = shownRows(rows);
  if (shown.length === 0 && !refusal) return null;
  return (
    <>
      {shown.length > 0 && (
        <div className="chat-card__rows">
          {shown.map((row) => liveRow(row, state, onChange, disabled))}
        </div>
      )}
      {refusal && (
        <p className="chat-error chat-card__refusal" role="alert">
          {refusal}
        </p>
      )}
    </>
  );
}

/** One row of a live card, as its controls (or as plain text when it has none). */
function liveRow(
  row: ConsoleChatCardRow,
  state: RowControlState,
  onChange: (next: RowControlState) => void,
  disabled: boolean,
): JSX.Element {
  const kind = rowControlKind(row);
  if (kind.picker) {
    return (
      <KV
        key={row.key}
        label={row.label}
        value={
          <Select
            className="chat-card__select"
            name={ROW_PICK_PREFIX + row.key}
            aria-label={row.label}
            options={pickerOptions(row)}
            value={state.pick[row.key] ?? (typeof row.value === "string" ? row.value : "")}
            onValueChange={(value) => onChange({ ...state, pick: { ...state.pick, [row.key]: value } })}
            disabled={disabled}
          />
        }
      />
    );
  }
  if (!kind.toggle && !kind.text) {
    return <KV key={row.key ?? row.label} label={row.label} value={rowValueText(row)} />;
  }

  const before = typeof row.before === "string" ? row.before.trim() : "";
  const label = kind.text && before !== "" ? `${row.label} (now: ${before})` : row.label;
  const ticked = !kind.toggle || state.on[row.key] !== false;
  const boxWords = kind.text ? SAVE_THIS_ROW_TITLE : rowValueText(row);
  return (
    <KV
      key={row.key}
      label={label}
      value={
        <span className="chat-card__control">
          {kind.toggle && (
            <Checkbox
              name={ROW_TOGGLE_PREFIX + row.key}
              aria-label={`${row.label}: ${boxWords}`}
              label={boxWords}
              checked={ticked}
              onCheckedChange={(checked) => onChange({ ...state, on: { ...state.on, [row.key]: checked } })}
              disabled={disabled}
            />
          )}
          {kind.text && (
            <Textarea
              name={ROW_TEXT_PREFIX + row.key}
              aria-label={row.label}
              rows={3}
              maxLength={WRITE_CONFIRM_MAX_EDITABLE_CHARS}
              value={state.txt[row.key] ?? (typeof row.after === "string" ? row.after : "")}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                onChange({ ...state, txt: { ...state.txt, [row.key]: e.target.value } })
              }
              disabled={disabled || !ticked}
            />
          )}
        </span>
      }
    />
  );
}

/** One string off the preview's untyped `meta` bag, or null. Checked rather than cast: a
 *  missing key must degrade the caption, never render the word "undefined" beside a diff
 *  someone is about to approve. */
function metaString(meta: Record<string, unknown> | undefined, key: string): string | null {
  const value = meta?.[key];
  return typeof value === "string" && value !== "" ? value : null;
}

/**
 * The engine-computed preview on a write_confirm card, whatever its kind (card #2932).
 *
 * `unified_diff` is the one kind the console knows how to draw, so it gets the diff panel below,
 * unchanged. Every other kind (today the mail connectors' `attachment` line, tomorrow whatever a
 * new `IProposalPreviewProvider` emits) is shown as its `text`, verbatim and monospaced — the
 * fallback that provider contract promises. Dropping an unknown kind would hide the one line
 * the confirmer needs (which document goes out with this email) behind a Confirm button.
 *
 * Exported for `ChatScreen.preview.test.ts`, which renders it; nothing else imports it.
 */
export function WriteConfirmPreview({ preview }: { preview: ConsoleChatCardPreview }): JSX.Element | null {
  return preview.kind === DIFF_PREVIEW_KIND ? (
    <UnifiedDiffPreview preview={preview} />
  ) : (
    <TextPreview preview={preview} />
  );
}

/**
 * Card #2932 — a preview of a kind the console does not draw: its `text`, verbatim, in a
 * monospaced block inside the same `chat-card__preview` wrapper the diff uses. No caption: the
 * only caption the console knows how to write is the diff's version line, and `meta` is a
 * per-kind bag it cannot read honestly. The card is jsonb, so `text` is checked rather than
 * trusted: a missing or empty one renders nothing rather than an empty box.
 */
function TextPreview({ preview }: { preview: ConsoleChatCardPreview }): JSX.Element | null {
  const text: unknown = preview.text;
  if (typeof text !== "string" || text === "") return null;
  return (
    <div className="chat-card__preview">
      <pre className="chat-card__preview-text ard-mono">{text}</pre>
    </div>
  );
}

/**
 * Phase 482 wave 3 task 3.1 (ADR-098, AC-5) — the engine-computed diff on a write_confirm card.
 *
 * <b>Why this sits above the buttons.</b> `card.prompt` is the MODEL's sentence about what it
 * proposed; `card.preview` is the ENGINE's rendering of the bound args that will actually
 * execute on Confirm (the connector's own `IProposalPreviewProvider` preview). Only the second is
 * evidence. Putting it between the prompt and the actions is the whole point of AC-5: the last
 * thing under the operator's eye before Confirm is the change itself.
 *
 * The panel renders nothing of its own here — no re-wrapping, no truncation, no summary. The
 * diff text is displayed verbatim; the caption states only facts the engine put in `meta`.
 */
function UnifiedDiffPreview({ preview }: { preview: ConsoleChatCardPreview }): JSX.Element | null {
  const lines = React.useMemo(() => {
    // The card is jsonb: the DTO's `text: string` is the engine's promise, not the database's
    // guarantee. One malformed row must cost this card its diff, never the whole thread.
    const text = typeof preview.text === "string" ? preview.text : "";
    return splitDiff(text).map((line) => ({ text: line, cls: classifyDiffLine(line) }));
  }, [preview.text]);

  // An empty diff means the edits changed nothing. There is no diff to show and no honest way
  // to draw one, so the card falls back to the prompt + buttons it had before this phase.
  if (lines.length === 0) return null;

  const from = metaString(preview.meta, "from_version") ?? "unknown";
  const to = metaString(preview.meta, "to_version") ?? "unknown";
  // `meta.warnings` is the validator's warning list (objects, not strings) — the caption
  // reports HOW MANY there are; reading them is `ardept workflow show --source`'s job.
  const warningsValue = preview.meta?.["warnings"];
  const warningCount = Array.isArray(warningsValue) ? warningsValue.length : 0;

  return (
    <div className="chat-card__preview">
      <p className="chat-card__preview-caption">
        {from} → {to} · warnings: {warningCount}
      </p>
      <DiffBlock lines={lines} label={`Unified diff, ${from} to ${to}`} />
    </div>
  );
}
